// src/features/collaboration/components/ShareMenu.js - Update the component

import React, { useState } from 'react';
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Box,
  TextField,
  Button,
  IconButton,
  Snackbar,
  Alert,
  Tooltip
} from '@mui/material';
import {
  Twitter as TwitterIcon,
  Facebook as FacebookIcon,
  LinkedIn as LinkedInIcon,
  Email as EmailIcon,
  Link as LinkIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Share as ShareIcon,
  WhatsApp as WhatsAppIcon,
  Telegram as TelegramIcon
} from '@mui/icons-material';
import { THEME_COLORS } from '../../../styles/theme';
import { 
  generateSharingUrl, 
  shareViaNavigator,
  generateShareData 
} from '../services/sharingService';

const ShareMenu = ({ 
  anchorEl, 
  open, 
  onClose, 
  post, 
  onShare,
  baseUrl = window.location.origin
}) => {
  const [copied, setCopied] = useState(false);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [alertState, setAlertState] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  const handleShare = async (platform) => {
    try {
      const shareData = generateShareData(post, baseUrl);
      if (!shareData) {
        setAlertState({
          open: true,
          message: `Unable to share post. Missing data.`,
          severity: 'error'
        });
        return;
      }
      
      // Try native sharing API first for mobile devices
      if (platform === 'native') {
        // Prepare data for Navigator.share API
        const navigatorShareData = {
          title: shareData.title,
          text: shareData.text,
          url: shareData.url
        };
        
        // Add image if available and supported by the browser
        if (shareData.imageUrl && navigator.canShare && 
            navigator.canShare({ files: [new File([''], 'file.jpg', { type: 'image/jpeg' })] })) {
          try {
            // Fetch the image and convert to File object
            const response = await fetch(shareData.imageUrl);
            const blob = await response.blob();
            const file = new File([blob], 'shared-image.jpg', { type: blob.type });
            navigatorShareData.files = [file];
          } catch (imgError) {
            console.error("Error fetching image for native sharing:", imgError);
            // Continue without the image if there's an error
          }
        }
        
        const shared = await shareViaNavigator(navigatorShareData);
        if (shared) {
          if (onShare) {
            await onShare(post.id, 'native');
          }
          
          onClose();
          return;
        } else {
          // If native sharing fails, show the copy link option
          setShowLinkInput(true);
          return;
        }
      }
      
      // For specific platforms, open in a new window
      if (['twitter', 'facebook', 'linkedin', 'whatsapp', 'telegram'].includes(platform)) {
        const shareUrl = generateSharingUrl(platform, shareData);
        window.open(shareUrl, '_blank', 'width=600,height=400');
        
        if (onShare) {
          await onShare(post.id, platform);
        }
        
        setAlertState({
          open: true,
          message: `Post shared to ${platform.charAt(0).toUpperCase() + platform.slice(1)}!`,
          severity: 'success'
        });
      } 
      // For email, open the mail client
      else if (platform === 'email') {
        const mailtoUrl = generateSharingUrl('email', shareData);
        window.location.href = mailtoUrl;
        
        if (onShare) {
          await onShare(post.id, 'email');
        }
      } 
      // For copy link
      else if (platform === 'copy') {
        await navigator.clipboard.writeText(shareData.url);
        setCopied(true);
        
        if (onShare) {
          await onShare(post.id, 'copy');
        }
        
        setAlertState({
          open: true,
          message: 'Link copied to clipboard!',
          severity: 'success'
        });
        
        // Reset copied state after 2 seconds
        setTimeout(() => {
          setCopied(false);
        }, 2000);
      }
      
      onClose();
    } catch (error) {
      console.error(`Error sharing to ${platform}:`, error);
      setAlertState({
        open: true,
        message: `Failed to share post. Please try again.`,
        severity: 'error'
      });
    }
  };
  
  const handleCloseAlert = () => {
    setAlertState({ ...alertState, open: false });
  };
  
  // Rest of the component remains the same
  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            width: 240,
            p: 1,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }
        }}
      >
        <Typography variant="subtitle2" fontWeight="bold" sx={{ px: 2, py: 1 }}>
          Share Post
        </Typography>
        
        <Divider sx={{ mb: 1 }} />
        
        {showLinkInput ? (
          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              value={`${baseUrl}/post/${post.id}`}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <Tooltip title={copied ? "Copied!" : "Copy link"}>
                    <IconButton 
                      edge="end" 
                      onClick={() => handleShare('copy')}
                      sx={{ color: copied ? 'success.main' : 'inherit' }}
                    >
                      {copied ? <CheckIcon /> : <CopyIcon />}
                    </IconButton>
                  </Tooltip>
                ),
              }}
              sx={{ mb: 1 }}
            />
            <Button 
              variant="outlined" 
              fullWidth 
              onClick={() => setShowLinkInput(false)}
              sx={{ mt: 1 }}
            >
              Back to options
            </Button>
          </Box>
        ) : (
          <>
            <MenuItem onClick={() => handleShare('twitter')}>
              <ListItemIcon>
                <TwitterIcon sx={{ color: '#1DA1F2' }} />
              </ListItemIcon>
              <ListItemText>Twitter</ListItemText>
            </MenuItem>
            
            <MenuItem onClick={() => handleShare('facebook')}>
              <ListItemIcon>
                <FacebookIcon sx={{ color: '#4267B2' }} />
              </ListItemIcon>
              <ListItemText>Facebook</ListItemText>
            </MenuItem>
            
            <MenuItem onClick={() => handleShare('linkedin')}>
              <ListItemIcon>
                <LinkedInIcon sx={{ color: '#0077B5' }} />
              </ListItemIcon>
              <ListItemText>LinkedIn</ListItemText>
            </MenuItem>
            
            <MenuItem onClick={() => handleShare('whatsapp')}>
              <ListItemIcon>
                <WhatsAppIcon sx={{ color: '#25D366' }} />
              </ListItemIcon>
              <ListItemText>WhatsApp</ListItemText>
            </MenuItem>
            
            <MenuItem onClick={() => handleShare('telegram')}>
              <ListItemIcon>
                <TelegramIcon sx={{ color: '#0088cc' }} />
              </ListItemIcon>
              <ListItemText>Telegram</ListItemText>
            </MenuItem>
            
            <MenuItem onClick={() => handleShare('email')}>
              <ListItemIcon>
                <EmailIcon sx={{ color: THEME_COLORS.primary }} />
              </ListItemIcon>
              <ListItemText>Email</ListItemText>
            </MenuItem>
            
            <Divider sx={{ my: 1 }} />
            
            <MenuItem onClick={() => handleShare('copy')}>
              <ListItemIcon>
                <LinkIcon sx={{ color: 'text.primary' }} />
              </ListItemIcon>
              <ListItemText>Copy Link</ListItemText>
            </MenuItem>
            
            <MenuItem onClick={() => handleShare('native')}>
              <ListItemIcon>
                <ShareIcon sx={{ color: THEME_COLORS.primary }} />
              </ListItemIcon>
              <ListItemText>More Options</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>
      
      <Snackbar
        open={alertState.open}
        autoHideDuration={3000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseAlert} 
          severity={alertState.severity} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {alertState.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ShareMenu;
